// Client-side image types and helpers. The URL resolver (`mediaImage`) lives
// in media.ts.

export type MediaImage = {
  src: string
  srcSet?: string
  width?: number
  height?: number
}

const warmed = new Map<string, HTMLImageElement>()

/** The Data Saver setting; `navigator.connection` isn't in lib.dom (Chromium only). */
function saveData() {
  const connection: unknown =
    'connection' in navigator ? navigator.connection : undefined
  return (
    typeof connection === 'object' &&
    connection !== null &&
    'saveData' in connection &&
    connection.saveData === true
  )
}

/**
 * Starts downloading the variant the browser would pick for each image at the
 * given `sizes` (same srcset/sizes resolution as an <img>), so it is already
 * in the HTTP cache when the real card renders. Browser-only; skipped when the
 * user has Data Saver on. Safe to call repeatedly with the same images.
 */
export function preloadImages(
  images: (MediaImage | undefined)[],
  sizes: string,
) {
  if (typeof window === 'undefined') return
  if (saveData()) return
  for (const attrs of images) {
    if (!attrs || warmed.has(attrs.src)) continue
    const img = new Image()
    img.decoding = 'async'
    img.sizes = sizes
    if (attrs.srcSet) img.srcset = attrs.srcSet
    img.src = attrs.src
    // Keep a reference so the load isn't cancelled by GC; cap the memory.
    warmed.set(attrs.src, img)
    if (warmed.size > 80) {
      const oldest = warmed.keys().next().value
      if (oldest !== undefined) warmed.delete(oldest)
    }
  }
}
